import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAssessment } from '../context/AssessmentContext';
import { Activity, User, Calendar, AlertCircle, Hash } from 'lucide-react';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { startAssessment } = useAssessment();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    age: '',
    gender: '',
    medicalConditions: '',
    splinkId: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Redirect if not authenticated
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.age || !formData.gender) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    try {
      // Save onboarding data and link Splink ID if provided
      const response = await fetch('/api', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'user.onboard',
          data: {
            email: user.email,
            stackUserId: user.id,
            firstName: formData.firstName,
            lastName: formData.lastName,
            age: parseInt(formData.age),
            gender: formData.gender,
            medicalConditions: formData.medicalConditions || null,
            splinkId: formData.splinkId || null
          }
        })
      });

      const result = await response.json();

      if (result.success) {
        // Update user in localStorage with onboarding data
        const updatedUser = {
          ...user,
          firstName: formData.firstName,
          lastName: formData.lastName,
          displayName: `${formData.firstName} ${formData.lastName}`,
          onboarded: true,
          athleteLinked: result.athleteLinked || false,
          splinkId: result.splinkId || null
        };
        localStorage.setItem('auth_user', JSON.stringify(updatedUser));

        // Start assessment
        await startAssessment({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: user.email,
          age: formData.age,
          gender: formData.gender,
          medicalConditions: formData.medicalConditions,
          splinkId: formData.splinkId
        });

        // Navigate to assessment
        navigate('/assessment');
      } else {
        setError(result.error || 'Failed to save onboarding data');
        setLoading(false);
      }
    } catch (err) {
      console.error('Onboarding error:', err);
      setError('An error occurred. Please try again.');
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Activity className="w-12 h-12 text-blue-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Welcome to Splink Pro!</h1>
          <p className="text-slate-400">Let's get you started with a few quick details</p>
        </div>

        {/* Form Card */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  First Name <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                    placeholder="Enter first name"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Last Name <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Age and Gender */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Age <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    min="5"
                    max="100"
                    className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                    placeholder="Enter age"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Gender <span className="text-red-400">*</span>
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors"
                  required
                >
                  <option value="">Select gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {/* Medical Conditions */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Medical Conditions (Optional)
              </label>
              <div className="relative">
                <AlertCircle className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                <textarea
                  name="medicalConditions"
                  value={formData.medicalConditions}
                  onChange={handleChange}
                  rows="3"
                  className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors resize-none"
                  placeholder="Any medical conditions that may affect your assessment? (e.g., asthma, recent injuries)"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                This helps us provide safer and more personalized recommendations
              </p>
            </div>

            {/* Splink ID */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Splink ID (Optional)
              </label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  name="splinkId"
                  value={formData.splinkId}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="e.g., SPL-PAT-2024-001"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                If you received a Splink ID via email, enter it here to link your account
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-900/20 border border-red-600 rounded-lg p-4">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white" />
                  <span>Setting up your account...</span>
                </>
              ) : (
                <span>Start Assessment</span>
              )}
            </button>
          </form>

          {/* Info Box */}
          <div className="mt-6 bg-blue-900/20 border border-blue-600/30 rounded-lg p-4">
            <p className="text-sm text-blue-300">
              <strong>What's next?</strong> After completing this form, you'll take three assessments:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-slate-400">
              <li>• Nutrition questionnaire (~5 minutes)</li>
              <li>• Cognitive assessment (~5 minutes)</li>
              <li>• Physical tests with camera (~10 minutes)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
